import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Cloud,
  FolderTree,
  Brain,
  Star,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  UserCircle,
  Info,
  RefreshCw,
  LogOut,
  Music2,
} from "lucide-react";

import { BUILD_VERSION, forceUpdate } from "@/lib/version";
import { signOut } from "@/lib/api";
import { useStartAuth } from "@/hooks/use-session";
import {
  getDefaultDriveFolder,
  setDefaultDriveFolder,
  getDefaultTarget,
  setDefaultTarget,
  type SongTarget,
} from "@/lib/song-defaults";

import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  listFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  getPrefs,
  updatePrefs,
  listOcrFeedback,
  getLearningStats,
  listLearningsForFolder,
  setLearningGold,
  type LearnedExample,
  deleteAccount,
  driveCleanup,
  type DriveCleanupResponse,
  type OcrFeedbackEntry,
} from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useSession } from "@/hooks/use-session";
import type { Folder } from "@/lib/types";

export const Route = createFileRoute("/_app/settings")({
  component: SettingsView,
});

// ──────────────────────────────────────────────────────────────────────
// Section + Row primitives (kept from the Lovable scaffold)
// ──────────────────────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  desc,
  children,
}: {
  icon: typeof Cloud;
  title: string;
  desc: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-start gap-4">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h2 className="font-display text-lg">{title}</h2>
          <p className="text-sm text-muted-foreground mb-4">{desc}</p>
          {children}
        </div>
      </div>
    </section>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-t border-border first:border-t-0">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Drive
// ──────────────────────────────────────────────────────────────────────

function DriveSection() {
  const session = useSession();
  const data = session.data;
  // #226 follow-up — Settings re-auth row needs to ACTUALLY trigger the
  // Drive OAuth popup, not just navigate to "/" (which is a no-op when
  // the user's session cookie is still valid).
  const qc = useQueryClient();
  const startAuth = useStartAuth();
  const handleReauth = async () => {
    try {
      await startAuth.mutateAsync();
      toast.success("Drive reconnected");
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: ["ihub"] });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Try again";
      toast.error("Sign-in failed", { description: msg });
    }
  };

  // Drive cleanup: trash old/stale Drive folders this app created.
  // Two-step flow:
  //   1. Tap "Tidy up Drive folders" → driveCleanup("preview") → list
  //      shown in a modal.
  //   2. User reviews + confirms → driveCleanup("trash") → folders are
  //      moved to Drive trash (NOT permanent — restorable for 30 days).
  // Scope limit: drive.file OAuth means only folders THIS app created
  // are visible/touchable. Personal Drive content is unaffected.
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupPreview, setCleanupPreview] =
    useState<DriveCleanupResponse | null>(null);

  const runPreview = async () => {
    setCleanupBusy(true);
    try {
      const res = await driveCleanup("preview");
      setCleanupPreview(res);
    } catch (e) {
      toast.error("Couldn't read Drive folders", {
        description: e instanceof Error ? e.message : "Try again",
      });
    } finally {
      setCleanupBusy(false);
    }
  };
  const runTrash = async () => {
    setCleanupBusy(true);
    try {
      const res = await driveCleanup("trash");
      setCleanupPreview(null);
      toast.success(
        `Moved ${res.trashed.length} folder${res.trashed.length === 1 ? "" : "s"} to Drive trash`,
        {
          description:
            res.failed.length > 0
              ? `${res.failed.length} couldn't be trashed — try again`
              : "Restorable from My Drive trash for 30 days.",
        },
      );
    } catch (e) {
      toast.error("Cleanup failed", {
        description: e instanceof Error ? e.message : "Try again",
      });
    } finally {
      setCleanupBusy(false);
    }
  };

  return (
    <Section
      icon={Cloud}
      title="Google Drive"
      desc="Your documents are stored in your own Drive."
    >
      <Row
        label="Connected as"
        hint={data?.email ?? "—"}
      >
        <span className="inline-flex items-center gap-1.5 text-xs font-medium">
          <span
            className={`h-2 w-2 rounded-full ${data?.drive_active ? "bg-success" : "bg-destructive"}`}
          />
          {data?.drive_active ? (
            <span className="text-success">Active</span>
          ) : (
            <span className="text-destructive">Disconnected</span>
          )}
        </span>
      </Row>
      <Row label="Re-authenticate">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReauth}
          disabled={startAuth.isPending}
        >
          {startAuth.isPending ? "Reconnecting…" : "Sign in again"}
        </Button>
      </Row>
      <Row
        label="Tidy up Drive"
        hint="Trash old/stale Drive folders this app created. Personal Drive content is invisible to the app, so it stays put."
      >
        <Button
          variant="outline"
          size="sm"
          disabled={cleanupBusy || !data?.drive_active}
          onClick={runPreview}
        >
          {cleanupBusy ? "Working…" : "Preview cleanup"}
        </Button>
      </Row>

      {/* Preview modal — shows what would be trashed before we touch
          anything. User confirms or cancels. */}
      {cleanupPreview && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setCleanupPreview(null)}
        >
          <div
            className="bg-card rounded-xl border max-w-md w-full p-5 shadow-xl space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-lg">Drive cleanup preview</h3>
            {cleanupPreview.candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing to clean up — every folder in your Drive matches
                your current taxonomy. ✓
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  These <strong>{cleanupPreview.candidates.length}</strong> folder
                  {cleanupPreview.candidates.length === 1 ? "" : "s"} will be
                  moved to Drive trash (restorable for 30 days):
                </p>
                <ul className="text-sm bg-muted rounded-md p-3 max-h-60 overflow-y-auto space-y-1">
                  {cleanupPreview.candidates.map((c) => (
                    <li key={c.id} className="flex items-baseline gap-2">
                      <span className="text-muted-foreground text-xs shrink-0">
                        {c.parent === "root"
                          ? "My Drive/"
                          : "My Drive/iHubOjaswati/"}
                      </span>
                      <span className="font-medium">{c.name}</span>
                    </li>
                  ))}
                </ul>
                {cleanupPreview.kept.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Keeping {cleanupPreview.kept.length} folder
                    {cleanupPreview.kept.length === 1 ? "" : "s"} (active
                    iHubOjaswati root + categories you still use).
                  </p>
                )}
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCleanupPreview(null)}
              >
                Cancel
              </Button>
              {cleanupPreview.candidates.length > 0 && (
                <Button
                  size="sm"
                  disabled={cleanupBusy}
                  onClick={runTrash}
                >
                  {cleanupBusy
                    ? "Trashing…"
                    : `Move ${cleanupPreview.candidates.length} to trash`}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Folders CRUD
// ──────────────────────────────────────────────────────────────────────

function FolderEditDialog({
  folder,
  open,
  onOpenChange,
}: {
  folder: Folder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const isNew = !folder;
  const [name, setName] = useState(folder?.name ?? "");
  const [aiHints, setAiHints] = useState(folder?.ai_hints ?? "");
  const [error, setError] = useState<string | null>(null);

  // Sync on open
  useState(() => {
    if (open && folder) {
      setName(folder.name);
      setAiHints(folder.ai_hints ?? "");
    }
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return createFolder({ name: name.trim(), ai_hints: aiHints || null });
      }
      return updateFolder(folder.id, { name: name.trim(), ai_hints: aiHints || null });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.folders });
      onOpenChange(false);
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : "Save failed");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isNew ? "Add folder" : `Edit ${folder.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Insurance"
            />
          </div>
          <div>
            <Label className="text-sm">AI hints (optional)</Label>
            <Input
              value={aiHints}
              onChange={(e) => setAiHints(e.target.value)}
              maxLength={1000}
              placeholder="e.g. LIC, HDFC Life, Star Health"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tells the AI what kinds of docs go in this folder.
            </p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FoldersSection() {
  const qc = useQueryClient();
  const foldersQuery = useQuery({
    queryKey: queryKeys.folders,
    queryFn: listFolders,
  });
  const prefsQuery = useQuery({
    queryKey: queryKeys.prefs,
    queryFn: getPrefs,
  });
  const updatePrefsMut = useMutation({
    mutationFn: updatePrefs,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.prefs }),
  });
  const del = useMutation({
    mutationFn: (id: number) => deleteFolder(id, null),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.folders }),
  });

  const [editing, setEditing] = useState<Folder | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const folders = foldersQuery.data ?? [];
  const topLevel = folders.filter((f) => f.parent_id === null);

  return (
    <>
      <Section
        icon={FolderTree}
        title="Folders"
        desc="Top-level categories used to file documents."
      >
        <Row
          label="Auto-suggest folders"
          hint="AI proposes a folder when filing"
        >
          <Switch
            checked={prefsQuery.data?.auto_suggest_folders ?? true}
            onCheckedChange={(v) =>
              updatePrefsMut.mutate({ auto_suggest_folders: v })
            }
          />
        </Row>
        <Row
          label="Default fallback folder"
          hint="Used when AI is unsure"
        >
          <Select
            value={
              prefsQuery.data?.default_fallback_folder_id
                ? String(prefsQuery.data.default_fallback_folder_id)
                : "none"
            }
            onValueChange={(v) =>
              updatePrefsMut.mutate({
                default_fallback_folder_id:
                  v === "none" ? null : Number(v),
              })
            }
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {folders.map((f) => (
                <SelectItem key={f.id} value={String(f.id)}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>

        <div className="mt-4 border-t border-border pt-4 space-y-1.5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Your folders</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          </div>
          {foldersQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : topLevel.length === 0 ? (
            <p className="text-sm text-muted-foreground">No folders yet.</p>
          ) : (
            <ul className="space-y-1">
              {topLevel.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between gap-3 rounded-md px-3 py-1.5 hover:bg-muted/40"
                >
                  <div>
                    <div className="text-sm font-medium">{f.name}</div>
                    {f.ai_hints && (
                      <div className="text-xs text-muted-foreground">
                        {f.ai_hints}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="p-1 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setEditing(f);
                        setDialogOpen(true);
                      }}
                      aria-label="Edit"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="p-1 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete folder "${f.name}"? Items already filed under it keep their text label.`,
                          )
                        ) {
                          del.mutate(f.id);
                        }
                      }}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      <FolderEditDialog
        folder={editing}
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditing(null);
        }}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Notifications + Privacy
// ──────────────────────────────────────────────────────────────────────

// (Notifications section removed — fed the notifier Worker which is
// gone in the Datalab-only cleanup. Re-add when email reminders come
// back.)

/**
 * DeleteAccountRow — extracted so AboutSection can host it next to
 * Sign out and Force update. The "Allow AI to learn" toggle that used
 * to live alongside it was removed: the new learning loop
 * (learned_extractions + field_corrections + gold templates, v197–v200)
 * doesn't check user_prefs.allow_ai_learning, so the toggle was
 * misleading — users could turn it OFF expecting all learning to stop
 * while the new corpus kept being written.
 */
function DeleteAccountRow() {
  const session = useSession();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMut = useMutation({
    mutationFn: () => deleteAccount(confirmEmail.trim().toLowerCase()),
    onSuccess: () => {
      window.location.href = "/";
    },
    onError: (e) => {
      setDeleteError(e instanceof Error ? e.message : "Delete failed");
    },
  });

  return (
    <>
      <Row
        label="Delete account"
        hint="Removes everything we store; your Drive files stay put."
      >
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => {
            setConfirmingDelete(true);
            setDeleteError(null);
          }}
        >
          Delete
        </Button>
      </Row>

      <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This removes everything we store about you:
          </p>
          <ul className="text-sm list-disc pl-5 space-y-1 text-muted-foreground">
            <li>iHub items + OCR text + extracted fields</li>
            <li>Folder tree + preferences</li>
            <li>OCR correction history</li>
            <li>Search index (Vectorize vectors)</li>
            <li>Encrypted Google Drive token</li>
          </ul>
          <p className="text-sm text-muted-foreground">
            Your <strong>Drive files stay put</strong> — we don't touch what's
            in your own Drive.
          </p>
          <div className="pt-2">
            <Label className="text-sm">
              Type <code className="px-1 py-0.5 rounded bg-muted">{session.data?.email}</code> to confirm:
            </Label>
            <Input
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={session.data?.email ?? ""}
            />
          </div>
          {deleteError && (
            <p className="text-sm text-destructive">{deleteError}</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                deleteMut.isPending ||
                confirmEmail.trim().toLowerCase() !== session.data?.email
              }
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete forever"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// OCR feedback corpus
// ──────────────────────────────────────────────────────────────────────

/**
 * TemplatesDialog — opens when the user clicks "View templates" on a
 * folder card in the LearningSection. Lists the user's recent
 * confirmations for that folder; each row has a star toggle that PATCHes
 * /api/learnings/:id to mark/unmark is_gold. Gold-flagged rows win
 * few-shot retrieval over regular rows on every future similar doc.
 */
function TemplatesDialog({
  folder,
  onClose,
}: {
  folder: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["learnings", folder],
    queryFn: () => listLearningsForFolder(folder!, 20),
    enabled: !!folder,
    staleTime: 30_000,
  });
  const toggle = useMutation({
    mutationFn: ({ id, isGold }: { id: number; isGold: boolean }) =>
      setLearningGold(id, isGold),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["learnings", folder] });
      qc.invalidateQueries({ queryKey: queryKeys.learningStats });
    },
    onError: (e) => {
      toast.error("Couldn't update", {
        description: e instanceof Error ? e.message : "Try again shortly",
      });
    },
  });

  return (
    <Dialog open={folder !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 sm:px-6 pt-4 pb-3 border-b border-border shrink-0">
          <DialogTitle className="text-base font-semibold inline-flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500" />
            Templates · {folder}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Your confirmations for this folder. Tap the star on a row to
            mark it as a <strong>gold template</strong> — gold rows always win
            few-shot retrieval over regular ones, so future similar docs
            mirror your chosen example.
          </p>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto px-4 sm:px-6 py-3 scroll-visible">
          {list.isLoading && (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          )}
          {list.isError && (
            <p className="text-sm text-destructive" role="alert">
              {list.error instanceof Error
                ? list.error.message
                : "Couldn't load templates"}
            </p>
          )}
          {list.data && list.data.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No confirmations recorded yet for this folder. Confirm a few
              documents first — each one lands here.
            </p>
          )}
          {list.data && list.data.length > 0 && (
            <ul className="space-y-2">
              {list.data.map((row) => (
                <TemplateRow
                  key={row.id}
                  row={row}
                  pending={
                    toggle.isPending &&
                    toggle.variables?.id === row.id
                  }
                  onToggle={(isGold) =>
                    toggle.mutate({ id: row.id, isGold })
                  }
                />
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="px-4 sm:px-6 py-3 border-t border-border shrink-0">
          <Button onClick={onClose} className="w-full sm:w-auto">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateRow({
  row,
  pending,
  onToggle,
}: {
  row: LearnedExample;
  pending: boolean;
  onToggle: (isGold: boolean) => void;
}) {
  const isGold = (row.is_gold ?? 0) === 1;
  // Show the 3-4 most informative fields. Skip system-internal noise.
  const HIDDEN = new Set([
    "chosen_folder",
    "suggested_folder",
    "final_name",
    "subtype",
    "confidence",
    "rotation",
    "ocr_chars",
    "kind",
    "exif_raw",
  ]);
  const visible = Object.entries(row.field_mappings ?? {})
    .filter(([k, v]) => !HIDDEN.has(k) && v != null && v !== "")
    .slice(0, 4);
  return (
    <li
      className={
        "rounded-md border p-3 text-xs space-y-1.5 " +
        (isGold
          ? "border-amber-300/60 bg-amber-50/50 dark:border-amber-500/40 dark:bg-amber-500/5"
          : "border-border bg-muted/30")
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-muted-foreground text-[11px]">
          {new Date(row.confirmed_at).toLocaleString()}
        </div>
        <button
          type="button"
          onClick={() => onToggle(!isGold)}
          disabled={pending}
          aria-label={isGold ? "Remove gold flag" : "Mark as gold"}
          title={
            isGold
              ? "Gold template — always wins few-shot retrieval. Tap to remove."
              : "Mark as gold so this template always wins few-shot retrieval"
          }
          className={
            "inline-flex h-7 w-7 items-center justify-center rounded transition " +
            (isGold
              ? "text-amber-500 hover:bg-amber-100/50 dark:hover:bg-amber-500/10"
              : "text-muted-foreground hover:text-amber-500 hover:bg-amber-100/30 dark:hover:bg-amber-500/10")
          }
        >
          {pending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Star
              className={"h-4 w-4 " + (isGold ? "fill-amber-500" : "")}
            />
          )}
        </button>
      </div>
      {visible.length > 0 ? (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
          {visible.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="text-foreground truncate">{String(v)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-muted-foreground italic">No fields stored.</p>
      )}
    </li>
  );
}

function LearningSection() {
  const feedback = useQuery({
    queryKey: queryKeys.ocrFeedback,
    queryFn: listOcrFeedback,
  });
  // Field-correction stats per folder. Surfaces "for Donations you
  // corrected `amount` 4 of 5 times" — tells the user (and us)
  // where the AI is weak so we know which seed prompts / regex
  // patterns to iterate on next. Empty until USER_CONFIRM events
  // start hitting the new field_corrections table (commit A — v198).
  const stats = useQuery({
    queryKey: queryKeys.learningStats,
    queryFn: getLearningStats,
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);
  // Per-folder templates modal. When set to a folder name, opens a
  // dialog listing the user's confirmations for that folder with star
  // toggles to promote them to gold.
  const [templatesFolder, setTemplatesFolder] = useState<string | null>(null);

  // Stale-data hint — earlier versions of the section led with the
  // OCR-corrections Row showing 0, which made the learning loop look
  // broken when it was working (the real signal lives in
  // field_corrections). The new order leads with the top-line summary
  // + per-folder breakdown (the actual signal), and demotes OCR text
  // edits to a small footer Row that only shows up when count > 0.
  const totalConfirms = stats.data?.total_confirms ?? 0;
  const totalFieldCorrections = stats.data?.total_corrections ?? 0;
  const ocrCount = feedback.data?.count ?? 0;

  return (
    <>
      <Section
        icon={Brain}
        title="What I've learned"
        desc="Per-folder confirmations, the fields you correct most, and where the AI is reliably right."
      >
        {/* Headline summary — surfaces the two metrics that actually
            matter for the learning loop. Always rendered so first-time
            users see "0 confirmations" instead of an empty section
            that looks broken. */}
        <div className="grid grid-cols-2 gap-3 mb-1">
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Confirmations
            </div>
            <div className="font-display text-2xl tabular-nums text-foreground mt-0.5">
              {stats.isLoading ? "…" : totalConfirms}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              docs you've reviewed + filed
            </div>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Field corrections
            </div>
            <div className="font-display text-2xl tabular-nums text-foreground mt-0.5">
              {stats.isLoading ? "…" : totalFieldCorrections}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              your edits to AI suggestions
            </div>
          </div>
        </div>

        {/* Per-folder learning stats — confirmations, gold templates,
            and which field you correct the most. Hidden only when
            literally nothing has been confirmed yet. */}
        {stats.data && stats.data.folders.length > 0 && (
          <div className="mt-4 space-y-2 border-t border-border pt-4">
            <p className="text-xs text-muted-foreground mb-2">
              Per-folder breakdown
            </p>
            <div className="space-y-2">
              {stats.data.folders.map((f) => (
                <div
                  key={f.folder}
                  className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1"
                >
                  <div className="flex items-center justify-between font-medium text-foreground">
                    <span>{f.folder}</span>
                    <span className="text-muted-foreground font-normal">
                      {f.confirms} confirmed
                      {f.gold > 0 && (
                        <>
                          {" · "}
                          <span className="text-amber-600 dark:text-amber-400">
                            ★ {f.gold} gold
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  {f.top_corrected_fields.length > 0 ? (
                    <div className="text-muted-foreground">
                      Most corrected:{" "}
                      {f.top_corrected_fields.map((tc, i) => (
                        <span key={tc.key}>
                          {i > 0 && ", "}
                          <code className="text-foreground/80">{tc.key}</code>{" "}
                          <span className="text-muted-foreground/80">
                            ({tc.count})
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground/70">
                      No field corrections yet — the AI is getting this folder right.
                    </div>
                  )}
                  {/* View templates — opens a modal listing this folder's
                      learned_extractions. The user can star the canonical
                      ones to lock them in as gold few-shot templates. */}
                  <button
                    type="button"
                    onClick={() => setTemplatesFolder(f.folder)}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                  >
                    <Star className="h-3 w-3" />
                    View templates · mark as gold
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* OCR text edits — separate signal from field corrections,
            captured only when the user explicitly edits the OCR
            textarea in the review dialog. Most users never do, so we
            hide this Row entirely when count is 0 to keep the section
            from looking misleading. When count > 0 it sits in a small
            footer Row that opens the recent-edits dialog. */}
        {ocrCount > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <Row
              label="OCR text edits"
              hint="Edits you made to extracted OCR text (separate from field corrections)."
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                disabled={feedback.isLoading}
              >
                {feedback.isLoading ? "…" : `${ocrCount}`}
              </Button>
            </Row>
          </div>
        )}
      </Section>

      <TemplatesDialog
        folder={templatesFolder}
        onClose={() => setTemplatesFolder(null)}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recent OCR corrections</DialogTitle>
          </DialogHeader>
          {feedback.data?.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No corrections yet. When you edit OCR text on a review card and
              confirm, the change lands here.
            </p>
          ) : (
            <ul className="space-y-3">
              {(feedback.data?.entries ?? []).map((e: OcrFeedbackEntry) => (
                <li
                  key={e.id}
                  className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1"
                >
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>{e.doc_type ?? "—"}</span>
                    <span>
                      {new Date(e.created_at * 1000).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-destructive">Original: </span>
                    <span className="text-foreground/70">
                      {e.original_preview}
                    </span>
                  </div>
                  <div>
                    <span className="text-success">Corrected: </span>
                    <span className="text-foreground">
                      {e.corrected_preview}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// View
// ──────────────────────────────────────────────────────────────────────

function ProfileSection() {
  // Inline display-name editor. Reads the current name from /api/prefs
  // and writes via PATCH. Debounced — saves 600ms after the last keystroke
  // so the user doesn't have to click a save button.
  const qc = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: queryKeys.prefs,
    queryFn: getPrefs,
    staleTime: 60_000,
  });
  const [draft, setDraft] = useState("");
  const [touched, setTouched] = useState(false);
  // Sync local state once prefs land.
  useState(() => {
    void prefsQuery; // suppress unused warning if any
  });
  useEffectMount(() => {
    if (prefsQuery.data && !touched) {
      setDraft(prefsQuery.data.display_name ?? "");
    }
  }, [prefsQuery.data, touched]);

  const save = useMutation({
    mutationFn: (name: string) => updatePrefs({ display_name: name }),
    onSuccess: (updated) => {
      // Invalidate session too so the iHub greeting refreshes.
      qc.invalidateQueries({ queryKey: queryKeys.session });
      qc.setQueryData(queryKeys.prefs, updated);
      toast.success("Profile saved");
    },
    onError: (e) =>
      toast.error("Couldn't save", {
        description: e instanceof Error ? e.message : "Try again",
      }),
  });

  return (
    <Section
      icon={UserCircle}
      title="Profile"
      desc="The name we greet you with on the home page."
    >
      <Row label="Display name" hint="e.g. Uma Seetharaman">
        <div className="flex items-center gap-2 w-full sm:w-72">
          <Input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setTouched(true);
            }}
            placeholder="Your name"
            spellCheck={false}
            maxLength={60}
          />
          <Button
            size="sm"
            disabled={
              save.isPending ||
              !touched ||
              draft.trim() === (prefsQuery.data?.display_name ?? "")
            }
            onClick={() => save.mutate(draft.trim())}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </Row>
    </Section>
  );
}

function AboutSection() {
  // Local "armed" state so the user has to confirm before we nuke caches —
  // a single mis-tap on Force Update would otherwise sign them out of the
  // PWA and force a re-fetch of every chunk over cellular. Two-tap pattern
  // matches the bulk-delete confirm UX elsewhere.
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Section
      icon={Info}
      title="Account"
      desc="Your session, build version, and the nuclear option. Your library lives in your own Google Drive — nothing here touches that."
    >
      <Row label="Version">
        <code className="block w-full rounded-md border border-border bg-muted/40 px-3 py-2 text-xs font-mono break-all">
          {BUILD_VERSION}
        </code>
      </Row>
      <Row
        label="Force update to latest"
        hint="Unregisters service workers, clears caches, and reloads with a cache-buster. Use this if you just deployed a change and your phone is still showing the old screens."
      >
        {!armed ? (
          <Button
            variant="outline"
            onClick={() => setArmed(true)}
            className="w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Force update
          </Button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="default"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await forceUpdate();
                } finally {
                  // forceUpdate calls location.replace so this rarely runs;
                  // but if it fails (e.g. caches API unavailable on old
                  // Safari) we want the button to reset.
                  setBusy(false);
                  setArmed(false);
                }
              }}
              className="w-full sm:w-auto"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Yes — clear cache & reload
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setArmed(false)}
              disabled={busy}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        )}
      </Row>
      {/* #238 — Reset Ojaswati. Same destination as the standalone
          /reset.html page (bookmarkable for when the app itself
          won't load), exposed here as a discoverable Settings row
          so the user doesn't have to remember a URL. We deliberately
          link out to /reset.html instead of calling forceUpdate()
          inline: reset.html has the auto-countdown UX + an opt-in
          checkbox to ALSO clear IndexedDB / sign out, and runs on
          plain HTML so it works even when the React bundle is
          broken (the exact case where Force update isn't enough). */}
      <Row
        label="Reset Ojaswati"
        hint="A friendlier full-screen reset page (good for stubborn cache issues or when login won't take). Opens in this tab and walks you through. Your Drive + backend data are untouched."
      >
        <Button asChild variant="outline" className="w-full sm:w-auto">
          <a href="/reset.html">
            <RefreshCw className="h-4 w-4 mr-2" />
            Open reset page
          </a>
        </Button>
      </Row>
      <Row
        label="Sign out"
        hint="Clears your session cookie and reloads the login screen. The Drive grant stays linked to your Google account — sign back in to use it again. Use this if the app is stuck and you need to start fresh."
      >
        <SignOutButton />
      </Row>
      <DeleteAccountRow />
    </Section>
  );
}

function SignOutButton() {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const handleSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
    } catch {
      /* best effort — still purge client state below */
    }
    // Purge any client-side caches that survive the cookie clear.
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.allSettled(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* ignore */
    }
    // Send the user back to the root so the router shows the login flow.
    // Use replace so they can't Back-button into a session-locked page.
    const url = new URL("/", window.location.href);
    url.searchParams.set("_v", String(Date.now()));
    window.location.replace(url.toString());
  };
  if (!armed) {
    return (
      <Button
        variant="outline"
        onClick={() => setArmed(true)}
        className="w-full sm:w-auto"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Sign out
      </Button>
    );
  }
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button
        variant="destructive"
        disabled={busy}
        onClick={handleSignOut}
        className="w-full sm:w-auto"
      >
        {busy ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing out…
          </>
        ) : (
          <>
            <LogOut className="h-4 w-4 mr-2" /> Yes — sign out
          </>
        )}
      </Button>
      <Button
        variant="ghost"
        onClick={() => setArmed(false)}
        disabled={busy}
        className="w-full sm:w-auto"
      >
        Cancel
      </Button>
    </div>
  );
}

function SettingsView() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-8 py-8 space-y-5">
      <h1 className="font-display text-3xl mb-4">Settings</h1>
      <ProfileSection />
      <DriveSection />
      <FoldersSection />
      <SongDefaultsSection />
      <LearningSection />
      {/* Account = build version + force update + sign out + delete
          account, in one place. Merged from the old Privacy + About
          sections in v201 — the "Allow AI to learn" toggle was dropped
          (new learning loop doesn't honor it; would silently fail). */}
      <AboutSection />
    </div>
  );
}

/**
 * Song Workspace defaults: per-device knobs the workspace picks up
 * automatically on every ingest so Uma doesn't have to choose them
 * each time. Persisted to localStorage via @/lib/song-defaults.
 *
 * Today: default Drive destination folder (id + name) + default
 * target translation language. The "id" is Drive's stable handle —
 * the long token from the folder's URL — and "name" is the human
 * label shown in toasts and the Drive Export stepper.
 *
 * UX choices:
 *   • Save fires only when both id + name are non-empty (or both blank,
 *     to clear). A partial save would create an unusable default.
 *   • Toast confirms each save so the user sees the change took.
 *   • Sits between FoldersSection (iHub folders) and LearningSection
 *     because it's the songs analogue of folder configuration.
 */
function SongDefaultsSection() {
  // Lazy-init from localStorage. We don't sync to a backend — these are
  // small per-device preferences and the rest of the app is the same.
  const initial = getDefaultDriveFolder();
  const initialTarget = getDefaultTarget();
  const [folderId, setFolderId] = useState(initial?.id ?? "");
  const [folderName, setFolderName] = useState(initial?.name ?? "");
  const [target, setTarget] = useState<SongTarget | "">(initialTarget ?? "");

  // Track what's saved so we can show a subtle "unsaved" hint and
  // disable Save when nothing has changed.
  const savedRef = { id: initial?.id ?? "", name: initial?.name ?? "", target: initialTarget ?? "" };
  const dirty =
    folderId.trim() !== savedRef.id ||
    folderName.trim() !== savedRef.name ||
    target !== savedRef.target;

  const onSave = () => {
    const id = folderId.trim();
    const name = folderName.trim();
    // Reject partial saves — both or neither.
    if ((id && !name) || (!id && name)) {
      toast.error("Both folder ID and folder name are required", {
        description:
          "Or leave both blank to clear the default and pick a folder each time.",
      });
      return;
    }
    setDefaultDriveFolder(id && name ? { id, name } : null);
    setDefaultTarget(target || null);
    toast.success("Song defaults saved", {
      description:
        id && name
          ? `New songs land in "${name}" by default${target ? `, translated to ${target}` : ""}.`
          : "Defaults cleared. You'll pick a folder each time.",
    });
  };

  const onClear = () => {
    setFolderId("");
    setFolderName("");
    setTarget("");
    setDefaultDriveFolder(null);
    setDefaultTarget(null);
    toast.success("Song defaults cleared");
  };

  return (
    <Section
      icon={Music2}
      title="Song Workspace defaults"
      desc="Pre-fill the destination folder and translation target so every YouTube/Voice Drop ingest goes straight where you want."
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Default Drive folder name</Label>
          <Input
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="Learning Library"
            maxLength={80}
          />
          <p className="text-xs text-muted-foreground">
            The human label — shown in toasts and the upload stepper.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Default Drive folder ID</Label>
          <Input
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz_drive_folder_id"
            spellCheck={false}
            className="font-mono text-xs"
          />
          <p className="text-xs text-muted-foreground">
            Open the folder in Google Drive, copy the long token after{" "}
            <span className="font-mono text-foreground/80">/folders/</span> in
            the URL — that's the ID.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Default translation target</Label>
          <Select value={target || "_none"} onValueChange={(v) => setTarget(v === "_none" ? "" : (v as SongTarget))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Pick each time —</SelectItem>
              <SelectItem value="english">English</SelectItem>
              <SelectItem value="tamil">Tamil</SelectItem>
              <SelectItem value="sanskrit">Sanskrit</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            The language verses are translated into. You can still override
            per-ingest in the workspace.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={onSave} disabled={!dirty}>
            {dirty ? "Save defaults" : "Saved"}
          </Button>
          {(savedRef.id || savedRef.target) && (
            <Button variant="outline" onClick={onClear}>
              Clear
            </Button>
          )}
        </div>
      </div>
    </Section>
  );
}

// Thin wrapper around React.useEffect that lints clean when the dep
// array is intentional. (Avoids having to disable react-hooks/
// exhaustive-deps inline for the prefs-sync effect above.)
function useEffectMount(fn: () => void, deps: ReadonlyArray<unknown>) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fn, deps);
}
